import { map, catchError, distinctUntilChanged } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SessionStorageService } from '../services/session-storage.service';
import { EnvironmentService } from '../services/environment.service';
import { ErrorReportingService } from '../services/error-reporting.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { GridResults } from '../models/general-models';
import { RightBridgeApiService } from '../services/right-bridge-api.service';

@Injectable({ providedIn: 'root' })
export class BookManagementService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private envSvc = inject(EnvironmentService);
  private errSvc = inject(ErrorReportingService);
  private rbs = inject(RightBridgeApiService);

  private environment;
  public baseUrl: string;
  private org: string;

  private updateLaunchButton = new Subject();
  launchButton$ = this.updateLaunchButton.asObservable();

  private updateBmWidgets = new Subject();
  updateBmWidgets$ = this.updateBmWidgets.asObservable();
  private updateBmChips = new Subject();
  updateBmChips$ = this.updateBmChips.asObservable();
  private updateBmOptions = new Subject();
  updateBmOptions$ = this.updateBmOptions.asObservable();
  private clearBmFilters = new Subject();
  clearBmFilters$ = this.clearBmFilters.asObservable();
  private triggerViewSwitch = new Subject();
  triggerViewSwitch$ = this.triggerViewSwitch.asObservable();

  // Tracks which widgets are actively searching so the filter bar can show a
  // backdrop and block input until every widget finishes loading.
  private widgetKeySeq = 0;
  private widgetLoadingState = new Map<string, boolean>();
  private widgetsLoading = new BehaviorSubject<boolean>(false);
  // Only surface real transitions so subscribers don't churn on repeat emissions.
  widgetsLoading$ = this.widgetsLoading
    .asObservable()
    .pipe(distinctUntilChanged());

  public gridHeaders = [
    {
      DataField: 'EncryptedPolicyNum',
      Filter: true,
      Group: false,
      Hidden: false,
      Label: 'Policy #',
      RespVis: '',
      Sort: true,
      Type: 'string',
      FilterType: 'Search',
      Width: 98,
      externalFilter: {
        controlType: 'text',
        filterGroup: 'primary',
        filterType: 'contains',
        label: 'Policy Number',
        varName: 'PolicyNumber',
      },
    },
    {
      DataField: 'Carrier',
      Filter: true,
      Group: false,
      Hidden: false,
      Label: 'Carrier',
      RespVis: '',
      Sort: true,
      Type: 'string',
      FilterType: 'Dropdown',
      Width: 300,
      externalFilter: {
        controlType: 'dropdown',
        filterGroup: 'primary',
        filterType: 'contains',
        label: 'Carrier',
        options: [],
        varName: 'Carrier',
      },
    },
    {
      DataField: 'CarrierCode',
      Filter: false,
      Group: false,
      Hidden: false,
      Label: 'Carrier Code',
      RespVis: '',
      Sort: true,
      Type: 'string',
      Width: 105,
    },
    {
      DataField: 'Name',
      Filter: true,
      Group: false,
      Hidden: false,
      Label: 'Policy Name',
      RespVis: '',
      Sort: true,
      Type: 'string',
      FilterType: 'Search',
      Width: 150,
    },
    {
      DataField: 'PartyName',
      Filter: true,
      Group: false,
      Hidden: false,
      Label: 'Policy Owner',
      RespVis: '',
      Sort: true,
      Type: 'string',
      FilterType: 'Search',
      Width: 175,
      externalFilter: {
        controlType: 'text',
        filterGroup: 'primary',
        filterType: 'contains',
        label: 'Policy Owner',
        varName: 'PartyName',
      },
    },
    {
      DataField: 'PolicyStatus',
      Filter: true,
      Group: false,
      Hidden: false,
      Label: 'Policy Status',
      RespVis: '',
      Sort: true,
      Type: 'string',
      FilterType: 'Dropdown',
      Width: 100,
    },
    {
      DataField: 'ProductType',
      Filter: true,
      Group: false,
      Hidden: false,
      Label: 'Product Type',
      RespVis: '',
      Sort: true,
      Type: 'string',
      FilterType: 'Dropdown',
      Width: 100,
      externalFilter: {
        controlType: 'dropdown',
        filterGroup: 'primary',
        filterType: 'contains',
        label: 'Product Type',
        options: [],
        varName: 'ProductType',
      },
    },
    {
      DataField: 'ShareClass',
      Filter: true,
      Group: false,
      Hidden: false,
      Label: 'Share Class',
      RespVis: '',
      Sort: true,
      Width: 100,
      Type: 'string',
      FilterType: 'Dropdown',
    },
    {
      DataField: 'ContractValue',
      Filter: false,
      Group: false,
      Hidden: false,
      Label: 'Account Value',
      RespVis: '',
      Sort: true,
      Type: 'curr',
      Width: 115,
    },
    {
      DataField: 'IssueDate',
      Filter: true,
      Group: false,
      Hidden: false,
      Label: 'Issue Date',
      RespVis: '',
      Sort: true,
      Type: 'date',
      FilterType: 'DateRange',
      Width: 105,
    },
    {
      DataField: 'RefreshDate',
      Filter: false,
      Group: false,
      Hidden: false,
      Label: 'Last Updated',
      RespVis: '',
      Sort: true,
      Type: 'date',
      Width: 105,
    },
    {
      DataField: 'IRSCode',
      Filter: true,
      Group: false,
      Hidden: false,
      Label: 'IRS Code',
      RespVis: '',
      Sort: true,
      Type: 'string',
      FilterType: 'Dropdown',
      Width: 110,
      externalFilter: {
        controlType: 'dropdown',
        filterGroup: 'primary',
        filterType: 'contains',
        label: 'IRS Code',
        options: [],
        varName: 'IRSCode',
      },
    },
    {
      DataField: 'NIPR',
      Filter: false,
      Group: false,
      Hidden: false,
      Label: 'Producer Number',
      RespVis: '',
      Sort: true,
      Type: 'string',
      Width: 130,
    },
  ];

  constructor() {
    this.environment = this.envSvc.get();

    if (!this.environment) {
      this.envSvc.loadSettings();
      this.environment = this.envSvc.get();
    }

    this.baseUrl = this.environment.apiBase;
    this.org = this.environment.org || 'basev2';
  }

  updateLaunchButtonValues(values) {
    this.updateLaunchButton.next(values);
  }

  updateBmWidgetFilters(values) {
    this.updateBmWidgets.next(values);
  }

  updateBmChipsTrigger(values) {
    this.updateBmChips.next(values);
  }

  updateBmWidgetOptions(values) {
    this.updateBmOptions.next(values);
  }

  clearBmWidgetFilters(values) {
    this.clearBmFilters.next(values);
  }

  setViewSwitch(value) {
    this.triggerViewSwitch.next(value);
  }

  // Returns a unique key each widget uses to report its own loading state.
  nextWidgetKey(prefix = 'bm-widget'): string {
    return `${prefix}-${++this.widgetKeySeq}`;
  }

  // Widgets report load start/stop; emits true while any widget is loading.
  setWidgetLoading(widgetKey: string, isLoading: boolean): void {
    if (isLoading) {
      this.widgetLoadingState.set(widgetKey, true);
    } else {
      this.widgetLoadingState.delete(widgetKey);
    }
    this.widgetsLoading.next(this.widgetLoadingState.size > 0);
  }

  gridSetup(carrier = false): Observable<GridResults> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'pm')
      .set('X-RB-O', this.org);

    let url = this.baseUrl + `/PolicyManagement/GridSetup`;

    if (carrier) {
      url = url + '?carrier=true';
    }

    return this.http.get<GridResults>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  policyManagementData(data, carrier = false) {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'pm')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    let url = this.baseUrl + '/PolicyManagement/PolicySearch';

    if (carrier) {
      url = url + '?carrier=true';
    }

    const queryData = data;

    return this.http.post(url, queryData, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getPolicy(id, groupName) {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'pm')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl +
      `/PolicyManagement/Profile?profileID=${id}&groupName=${groupName}`;

    return this.http.get(url, { headers: reqHeaders }).pipe(
      map(res => {
        res['headers'] = this.gridHeaders;
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getPolicyBridgeOptions() {
    return this.rbs.postGroupProfile('UnitValues', 'PM', {});
  }

  downloadPolicyManagementData(data, carrier = false) {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'pm')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/csv');

    let url = this.baseUrl + '/PolicyManagement/PolicySearchCsv';

    if (carrier) {
      url = url + '?carrier=true';
    }

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  launchAwProfile(annuityArray) {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');

    const url = this.baseUrl + '/Profile';

    return this.http.post(url, annuityArray, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }
}
